<template>
  <checkout-section-card
    v-if="visible"
    variant="brand"
    :padded="false"
    data-testid="checkout-price-summary"
  >
    <div class="p-5">
      <!-- Product header -->
      <div v-if="checkoutStore.product" class="mb-5 flex items-center gap-3">
        <img
          v-if="
            checkoutStore.product.coverImage?.url &&
            isHttpsUrl(checkoutStore.product.coverImage.url)
          "
          :src="checkoutStore.product.coverImage.url"
          :alt="
            checkoutStore.product.coverImage.alternativeText ||
            checkoutStore.product.title
          "
          class="h-14 w-14 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
        />
        <div class="min-w-0">
          <p class="text-xl font-bold leading-tight text-mouse">
            {{ checkoutStore.product.title }}
          </p>
          <p v-if="checkoutStore.product.subHeadline" class="text-rhino">
            {{ checkoutStore.product.subHeadline }}
          </p>
        </div>
      </div>

      <p class="mb-3 text-xl font-semibold uppercase tracking-wider text-mouse">
        {{ t('checkout/configure/price/title') }}
      </p>

      <!-- Skeleton loader -->
      <div
        v-if="!checkoutStore.price"
        class="flex flex-col gap-3 animate-pulse"
      >
        <div class="flex items-center justify-between">
          <div class="h-3.5 w-20 rounded-md bg-depth-2"></div>
          <div class="h-3.5 w-16 rounded-md bg-depth-2"></div>
        </div>
        <div class="flex items-center justify-between">
          <div class="h-3.5 w-24 rounded-md bg-depth-2"></div>
          <div class="h-3.5 w-28 rounded-md bg-depth-2"></div>
        </div>
        <div class="flex items-center justify-between">
          <div class="h-3.5 w-14 rounded-md bg-depth-2"></div>
          <div class="h-3.5 w-16 rounded-md bg-depth-2"></div>
        </div>
        <div class="mt-2 h-16 rounded-lg bg-depth-2"></div>
      </div>

      <!-- Price data -->
      <div v-else class="flex flex-col">
        <!-- Config rows (Slots → Duration → Location) -->
        <div
          v-if="
            checkoutStore.selectedDurationLabel ||
            checkoutStore.selectedLocationName ||
            checkoutStore.selectedUnitsLabel
          "
          class="flex flex-col gap-2.5 pb-4"
        >
          <div
            v-if="checkoutStore.selectedUnitsLabel"
            class="flex items-center justify-between gap-2"
          >
            <span class="flex items-center gap-2 text-rhino">
              <span
                class="inline-block h-1 w-1 shrink-0 rounded-full bg-brand/60"
              ></span>
              {{ checkoutStore.unitTypeLabel }}
            </span>
            <span class="text-mouse">
              {{ checkoutStore.selectedUnitsLabel }}
            </span>
          </div>
          <div
            v-if="checkoutStore.selectedDurationLabel"
            class="flex items-center justify-between gap-2"
          >
            <span class="flex items-center gap-2 text-rhino">
              <span
                class="inline-block h-1 w-1 shrink-0 rounded-full bg-brand/60"
              ></span>
              {{ checkoutStore.summaryDurationLabel }}
            </span>
            <span class="text-mouse">
              {{ checkoutStore.selectedDurationLabel }}
            </span>
          </div>
          <div
            v-if="checkoutStore.selectedLocationName"
            class="flex items-center justify-between gap-2"
          >
            <span class="flex items-center gap-2 text-rhino">
              <span
                class="inline-block h-1 w-1 shrink-0 rounded-full bg-brand/60"
              ></span>
              {{ checkoutStore.summaryLocationLabel }}
            </span>
            <span class="text-right text-mouse">
              {{ checkoutStore.selectedLocationName }}
            </span>
          </div>
        </div>

        <!-- Price rows (subtotal + addons) -->
        <div class="flex flex-col gap-1.5 border-t border-white/5 pt-3">
          <div class="flex items-center justify-between">
            <span class="text-rhino">{{
              t('checkout/configure/price/subtotal')
            }}</span>
            <span class="text-rhino">{{
              format(checkoutStore.price.subtotal)
            }}</span>
          </div>

          <div
            v-if="checkoutStore.hasAddons"
            class="flex items-center justify-between"
          >
            <span class="text-rhino">{{
              t('checkout/configure/price/addons')
            }}</span>
            <span class="text-rhino">{{
              format(checkoutStore.price.addonsTotal)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Coupons section -->
      <div
        v-if="showCoupons"
        class="mt-2 flex flex-col gap-2 border-t border-white/5 pt-2"
      >
        <!-- Coupon input (interactive mode only) -->
        <template v-if="interactive">
          <div class="flex gap-2">
            <label for="price-coupon-input" class="sr-only">
              {{ t('checkout/configure/coupon/placeholder') }}
            </label>
            <input
              id="price-coupon-input"
              v-model="couponInput"
              type="text"
              data-testid="checkout-coupon-input"
              :disabled="isApplyingCoupon"
              class="min-w-0 flex-1 rounded-lg border-2 bg-depth-0 px-3 py-1.5 text-mouse placeholder:text-rhino focus:outline-none"
              :class="
                couponError
                  ? 'border-danger-fg focus:border-danger-fg'
                  : 'border-depth-3 focus:border-brand'
              "
              :placeholder="t('checkout/configure/coupon/placeholder')"
              @keydown.enter="onApplyCoupon"
              @input="couponError = ''"
            />
            <button
              type="button"
              data-testid="checkout-coupon-apply"
              :disabled="isApplyingCoupon"
              class="relative shrink-0 rounded-md border border-rhino bg-depth-2 px-3 py-1 text-sm font-semibold uppercase tracking-wider text-mouse transition-colors hover:bg-depth-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              @click="onApplyCoupon"
            >
              <span :class="{ 'opacity-0': isApplyingCoupon }">
                {{ t('checkout/configure/coupon/apply') }}
              </span>
              <span
                v-if="isApplyingCoupon"
                class="absolute inset-0 flex items-center justify-center"
              >
                <span
                  class="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent"
                ></span>
              </span>
            </button>
          </div>
          <p v-if="couponError" class="-mt-1 text-danger-fg">
            {{ couponError }}
          </p>
        </template>

        <!-- Applied coupons -->
        <div
          v-if="checkoutStore.couponCodes.length > 0"
          class="flex flex-col gap-1.5"
        >
          <div
            v-for="code in checkoutStore.couponCodes"
            :key="code"
            class="group flex items-center gap-2 rounded-lg px-3 py-2"
            :class="
              isErrorCoupon(code)
                ? 'bg-danger-fg/10 ring-1 ring-danger-fg/30'
                : 'bg-depth-0/40'
            "
          >
            <fa-icon
              :icon="faTag"
              class="size-3.5 shrink-0"
              :class="isErrorCoupon(code) ? 'text-danger-fg' : 'text-success'"
            />
            <span
              class="min-w-0 truncate font-mono text-sm font-semibold"
              :class="isErrorCoupon(code) ? 'text-danger-fg' : 'text-mouse'"
            >
              {{ code }}
            </span>
            <span
              v-if="couponDiscount(code)"
              class="ml-auto shrink-0 rounded-full bg-success px-2 py-0.5 text-sm font-bold leading-tight text-onyx"
            >
              -{{ couponDiscount(code) }}
            </span>
            <button
              v-if="interactive"
              type="button"
              class="shrink-0 cursor-pointer rounded-full p-1 transition-colors hover:bg-danger-fg/15 hover:text-danger-fg"
              :class="{
                'ml-auto': !couponDiscount(code),
                'text-danger-fg': isErrorCoupon(code),
                'text-rhino': !isErrorCoupon(code),
              }"
              :aria-label="t('checkout/configure/coupon/remove', { code })"
              @click="onRemoveCoupon(code)"
            >
              <fa-icon :icon="faTimes" class="size-3" />
            </button>
          </div>
        </div>

        <!-- Aggregate discount row -->
        <div
          v-if="
            checkoutStore.price &&
            checkoutStore.hasDiscount &&
            checkoutStore.couponCodes.length > 1
          "
          class="flex items-center justify-between"
        >
          <span class="text-rhino">{{
            t('checkout/configure/price/discount')
          }}</span>
          <span class="font-medium text-success">
            -{{ format(checkoutStore.price.discount) }}
          </span>
        </div>
      </div>

      <!-- Promotional credit: a discount before tax, so it lowers the total. -->
      <div
        v-if="promotionalCredit"
        class="mt-2 flex items-center justify-between border-t border-white/5 pt-2"
        data-testid="checkout-price-promotional-credit"
      >
        <span class="text-rhino">{{
          t('checkout/configure/price/promotional_credit')
        }}</span>
        <span class="font-medium text-success">
          -{{ format(promotionalCredit) }}
        </span>
      </div>

      <!-- Total -->
      <div
        v-if="checkoutStore.price"
        data-testid="checkout-price-total"
        class="mt-4 rounded-lg bg-depth-0/60 px-4 py-4"
      >
        <div class="flex items-end justify-between">
          <span
            class="text-lg font-semibold uppercase tracking-wider text-rhino"
          >
            {{ t('checkout/configure/price/total') }}
          </span>
          <div class="text-right">
            <span class="text-3xl font-bold text-brand">
              {{ format(invoiceTotal ?? checkoutStore.price.total) }}
            </span>
          </div>
        </div>
        <p v-if="convertedTotal" class="mt-1.5 text-sm text-rhino">
          {{
            t('checkout/configure/price/estimated', {
              amount: format(convertedTotal),
              currency: checkoutStore.price.total.currencyCode,
            })
          }}
        </p>
        <!-- The account balance is a payment against the total, after tax: it
             settles part of it, and what is left is due now. -->
        <div
          v-if="balanceApplied && amountDue"
          class="mt-3 flex flex-col gap-1.5 border-t border-white/5 pt-3"
          data-testid="checkout-price-balance"
        >
          <div class="flex items-center justify-between">
            <span class="text-rhino">{{
              t('checkout/configure/price/balance_applied')
            }}</span>
            <span class="text-rhino">-{{ format(balanceApplied) }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="font-semibold text-mouse">{{
              t('checkout/configure/price/amount_due')
            }}</span>
            <span class="font-semibold text-mouse">{{
              format(amountDue)
            }}</span>
          </div>
        </div>
      </div>
      <payment-unpaid-invoices-note
        v-if="hasUnpaidInvoices"
        class="mt-4"
        :text="t('checkout/step/create/unpaid_invoices')"
      />
      <p class="mt-2 text-sm text-rhino">
        {{ t('checkout/configure/price/vat') }}
      </p>
      <p v-if="showDiscountDisclaimer" class="mt-2 text-sm text-rhino">
        * {{ t('landingpage/discount/disclaimer') }}
      </p>
      <p class="mt-2 text-xs leading-relaxed text-rhino whitespace-pre-line">
        {{
          t('checkout/configure/price/cancellation', {
            company: billingCompany,
          })
        }}
      </p>
    </div>
  </checkout-section-card>
</template>

<script setup lang="ts">
import { faTag, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import { isHttpsUrl } from '~/helpers/checkout';

const props = withDefaults(
  defineProps<{
    interactive?: boolean;
  }>(),
  { interactive: true },
);

const { t } = useI18n();
const checkoutStore = useCheckoutStore();
const { format } = useNumberMoneyFormatting();

// How the logged-in customer's credits settle the order — present only for a
// balance-migrated customer (GetOrderPrice's credit_application). Each row is
// shown only when it moves money.
const creditApplication = computed(
  () => checkoutStore.price?.creditApplication,
);
function nonZero(money: Money | undefined): Money | null {
  return money && BigInt(money.amount) > 0n ? money : null;
}
const promotionalCredit = computed(() =>
  nonZero(creditApplication.value?.promotionalCreditsApplied),
);
// With promotional credit the invoice total is lower than the order price.
const invoiceTotal = computed(() =>
  promotionalCredit.value
    ? (creditApplication.value?.totalAfterCredits ?? null)
    : null,
);
// The estimate in the customer's currency, for the total shown above it. The
// conversion is of the order price, so with promotional credit it is scaled to
// the invoice total — it is an estimate either way.
const convertedTotal = computed<Money | null>(() => {
  const converted = checkoutStore.price?.convertedTotal;
  const total = checkoutStore.price?.total;
  if (!converted) return null;
  if (!invoiceTotal.value || !total || BigInt(total.amount) <= 0n) {
    return converted;
  }
  // No `display`: the server's preformatted string is of the unscaled amount.
  return new Money({
    amount:
      (BigInt(converted.amount) * BigInt(invoiceTotal.value.amount)) /
      BigInt(total.amount),
    currencyCode: converted.currencyCode,
    fraction: converted.fraction,
  });
});
const balanceApplied = computed(() =>
  nonZero(creditApplication.value?.balanceApplied),
);
const amountDue = computed(() =>
  balanceApplied.value ? (creditApplication.value?.amountDue ?? null) : null,
);
const { region } = useRegion();
const { hasUnpaidInvoices } = useUnpaidInvoices(
  () => checkoutStore.price?.total?.currencyCode,
);

// Region-dependent legal entity, so it can't live in the i18n copy; identical across locales.
const billingCompany = computed(() =>
  region.value === 'int' ? 'GPORTAL, Inc' : 'Ociris GmbH',
);

const couponInput = ref('');
const couponError = ref('');
const isApplyingCoupon = ref(false);

async function onApplyCoupon(): Promise<void> {
  const trimmed = couponInput.value.trim();
  if (!trimmed || isApplyingCoupon.value) return;

  couponError.value = '';
  isApplyingCoupon.value = true;

  try {
    const valid = await checkoutStore.addCoupon(trimmed);
    if (valid) {
      couponInput.value = '';
    } else {
      couponError.value = t('checkout/configure/coupon/invalid');
    }
  } catch {
    couponError.value = t('checkout/configure/coupon/invalid');
  } finally {
    isApplyingCoupon.value = false;
  }
}

async function onRemoveCoupon(code: string): Promise<void> {
  await checkoutStore.removeCoupon(code);
}

function isErrorCoupon(code: string): boolean {
  return checkoutStore.errorCouponCodes.includes(code);
}

const everHadPrice = ref(false);
watch(
  () => checkoutStore.price,
  (p) => {
    if (p) everHadPrice.value = true;
  },
  { immediate: true },
);

const hasContent = computed<boolean>(
  () =>
    !!checkoutStore.product ||
    !!checkoutStore.price ||
    !!checkoutStore.selectedLocationName ||
    !!checkoutStore.selectedDurationLabel ||
    !!checkoutStore.selectedUnitsLabel,
);

// Loading counts as visible, else the card's own skeleton never renders on first load.
const visible = computed<boolean>(() =>
  props.interactive
    ? everHadPrice.value || checkoutStore.isLoading
    : hasContent.value,
);

const showCoupons = computed<boolean>(
  () => checkoutStore.couponsAvailable || checkoutStore.couponCodes.length > 0,
);

const showDiscountDisclaimer = computed<boolean>(
  () => checkoutStore.hasDiscount,
);

function couponDiscount(code: string): string | null {
  if (!checkoutStore.hasDiscount || isErrorCoupon(code)) return null;

  const match = checkoutStore.appliedCoupons.find((c) => c.code === code);
  if (match?.discountAmount && Number(match.discountAmount.amount) > 0) {
    return format(match.discountAmount);
  }

  return null;
}
</script>
